/* eslint-disable no-new */
/* eslint-disable class-methods-use-this */
import Glightbox from 'glightbox';

import { mountApp } from '@van-wanrooij/interactive-map';

import Glide from '@glidejs/glide';
import { gsap } from 'gsap';
import { CSSRulePlugin } from 'gsap/CSSRulePlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import noUiSlider from 'nouislider';
import wNumb from 'wnumb';
import tippy from 'tippy.js';

import Header from './modules/Header';
import Menu from './modules/Menu';
import MediaSlider from './modules/MediaSlider';
import TimelineSlider from './modules/TimelineSlider';
import SingleLightbox from './modules/SingleLightbox';
import HouseTypesSlider from './modules/HouseTypesSlider';
import RelatedConstructionNumberSlider from './modules/RelatedConstructionNumberSlider';
import HandleToggle from './modules/Toggle';
import Mapbox from './modules/Mapbox';
import Tabs from './modules/Tabs';
import ContentToggle from './modules/ContentToggle';
import ShowCookies from './modules/nb-cookiepopup';
import Timeline from './modules/Timeline';
import SelectAutocomplete from './modules/SelectAutocomplete';
import Accordion from './modules/Accordion';
import SimpleAccordion from './modules/SimpleAccordion';
import InvolvedCompaniesSlider from './modules/InvolvedCompaniesSlider';
import FormSubmitDisable from './modules/FormSubmitDisable';
import { ExpandableItem } from './modules/ExpandableItem';
import { ExpandableInfoBlock } from './modules/ExpandableInfoBlock';
import NestedAccordion from './modules/NestedAccordion';
import SubscriptionSettings from './modules/SubscriptionSettings';
import { Fancybox } from '@fancyapps/ui';

/**
 *  App class
 *
 *  The App class functions a the main back-bone of the javascript used in this
 *  project.
 *
 */

class MainApp {
  /**
   *  Constructor
   */
  constructor() {
    this.onDocumentReady(this.init);
    tippy('[data-tippy-content]', {
      placement: 'top-end'
    });
  }

  onDocumentReady(fn) {
    if (
      document.readyState === 'complete' ||
      document.readyState === 'interactive'
    ) {
      // call on next available tick
      setTimeout(fn, 1);
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  showCookies() {
    new ShowCookies();
  }

  init() {
    Fancybox.bind('[data-fancybox]');

    const targetElement = document.querySelectorAll(
      '[data-app="interactive-map"]'
    );
    const apiKey =
      !process.env.NODE_ENV || process.env.NODE_ENV === 'production'
        ? 'pk.eyJ1IjoidmFud2Fucm9vaWpidiIsImEiOiJjanVhdXlwamEwNjdqNGRueHlsOTRhb3AzIn0.rlzR1nQd4LdngtmoHLVdKw'
        : 'pk.eyJ1IjoidmFud2Fucm9vaWpidiIsImEiOiJjbHMwMnl1Y3cxemZiMmltdTJlaml5NTZxIn0.gm5ITjyG7buFGsVMQ7_dyw';

    targetElement.forEach((element) => {
      mountApp(element, apiKey);
    });

    const expandableInfoBlock = document.querySelectorAll(
      '.js-info-block-expandable'
    );

    expandableInfoBlock.forEach((item) => new ExpandableInfoBlock(item));

    const glideElement = document.querySelector('.glide');
    if (glideElement) {
      new Glide('.glide', { type: 'carousel', gap: 0 }).mount();
    }
    new Header();
    new Menu();
    // eslint-disable-next-line no-new
    new HandleToggle(document.querySelectorAll('.js-toggle'));
    // Disable form submit
    new FormSubmitDisable();

    const menuWrapper = document.querySelector('.menu');
    const menuButton = document.querySelector('.header__nav__menu-button');

    /** check if we have a hash to scroll to! */
    const hash = window.location.hash.substring(1);

    /** if the anchor starts with 'anchor-', we scroll to it */
    if (hash.indexOf('anchor-') === 0) {
      const el = document.querySelector(`#${hash}`);
      if (el) el.scrollIntoView();
    }

    // CHROME ANCHOR ISSUE?
    // Workaround, not the best solution
    if (window.location.hash && navigator.userAgent.includes('Chrome')) {
      setTimeout(function () {
        var hash = window.location.hash;
        const el = document.querySelector(`${hash}`);
        if (el) {
          el.scrollIntoView();
        }
      }, 1000);
    }

    const expandableItems = document.querySelectorAll(
      '.js-header-item-expandable'
    );

    expandableItems.forEach((item) => {
      new ExpandableItem(item);
    });

    // DEFAULT GLIGHTBOX
    Glightbox({
      selector: '.glightbox',
      autoplayVideos: true
    });

    /** livingWishes form validator */
    const livingWishesForm = document.querySelector(
      'form[name="living_wishes_form"]'
    );

    if (livingWishesForm) {
      const submitButton = livingWishesForm.querySelector(
        'button[type="submit"]'
      );
      const regionsForm = livingWishesForm.querySelector(
        '#living_wishes_form_regions'
      );
      const housingTypesFrom = livingWishesForm.querySelector(
        '#living_wishes_form_housingTypes'
      );

      // Initialize checkboxes
      const regionCheckboxes = [
        ...regionsForm.querySelectorAll("input[type='checkbox']")
      ];
      const housingTypesCheckBoxes = [
        ...housingTypesFrom.querySelectorAll("input[type='checkbox']")
      ];

      let regionsChecked = 0;
      let housingTypesChecked = 0;

      // Function to update the submit button state
      const updateSubmitButtonState = () => {
        regionsChecked = regionCheckboxes.filter(
          (checkbox) => checkbox.checked
        ).length;
        housingTypesChecked = housingTypesCheckBoxes.filter(
          (checkbox) => checkbox.checked
        ).length;

        submitButton.disabled =
          regionsChecked === 0 || housingTypesChecked === 0;
      };

      // Initial check on page load
      const performInitialCheck = () => {
        updateSubmitButtonState();
      };
      window.addEventListener('load', performInitialCheck);

      // Add event listeners for manual interactions
      regionsForm.addEventListener('click', updateSubmitButtonState);
      housingTypesFrom.addEventListener('click', updateSubmitButtonState);
    }

    // GSAP
    const heroItems = gsap.utils.toArray('.js-hero-anim-item');
    if (heroItems.length) {
      gsap.set(heroItems, { visibility: 'visible' });
      gsap.from(heroItems, {
        autoAlpha: 0,
        y: 100,
        stagger: 0.1,
        delay: 1
      });
    }

    gsap.registerPlugin(ScrollTrigger, CSSRulePlugin);

    const scrollOuts = document.querySelectorAll('.js-scrollout');

    scrollOuts.forEach((element) => {
      document.addEventListener('scroll', function (e) {
        const posY = window.scrollY;

        /** 116 is height of header including pre-header */
        if (posY > 116) {
          const height = element.getBoundingClientRect().height;

          if (!element.getAttribute('scrolled')) {
            element.setAttribute('scrolled', 'true');
            element.style.top = `-${height}px`;
          }
        } else {
          if (element.getAttribute('scrolled')) {
            element.removeAttribute('scrolled');
            element.style.top = '0';
          }
        }
      });
    });

    const animGrids = document.querySelectorAll('.js-anim-grid');

    for (let i = 0; i < animGrids.length; i++) {
      const grid = animGrids[i];
      const items = grid.querySelectorAll('.js-anim-item');

      if (items.length) {
        gsap.set(items, { autoAlpha: 0, y: 48 });

        ScrollTrigger.batch(items, {
          onEnter: (elements) => {
            gsap.to(elements, {
              autoAlpha: 1,
              y: 0,
              duration: 0.3,
              ease: 'back.out(1.4)',
              stagger: {
                each: 0.1,
                grid: 'auto'
              }
            });
          },
          once: true,
          start: 'top 85%'
        });
      }
    }

    const headings = gsap.utils.toArray('.js-anim-heading');

    if (headings.length) {
      gsap.set(headings, { autoAlpha: 0, y: 48 });

      headings.forEach((heading) => {
        function addCompleteClass() {
          heading.classList.add('anim--after');
        }

        const tl = gsap.timeline({
          onComplete: addCompleteClass,
          scrollTrigger: {
            trigger: heading,
            start: 'top 90%',
            once: true
          }
        });
        tl.to(heading, { autoAlpha: 1, y: 0 });
      });
    }

    // TABS
    const tabElems = document.querySelectorAll('.js-tabs');
    if (tabElems.length) {
      for (let i = 0; i < tabElems.length; i++) {
        new Tabs(tabElems[i]);
      }
    }

    // TABS
    const contentToggleElems = document.querySelectorAll('.js-toggle-content');
    if (contentToggleElems.length) {
      for (let i = 0; i < contentToggleElems.length; i++) {
        new ContentToggle(contentToggleElems[i]);
      }
    }

    // ACCORDION
    const accordionElems = document.querySelectorAll('.js-accordion');
    if (accordionElems.length) {
      Array.from(accordionElems).forEach((accordion) => {
        new Accordion(accordion);
      });
    }

    // SIMPLE ACCORDION
    const simpleAccordionElems = document.querySelectorAll('.simple-accordion');
    if (simpleAccordionElems.length) {
      Array.from(simpleAccordionElems).forEach((accordion) => {
        new SimpleAccordion(accordion);
      });
    }

    // NESTEDACCORDION
    const nestedAccordionElems = document.querySelectorAll(
      '.js-nested-accordion'
    );
    if (nestedAccordionElems.length) {
      Array.from(nestedAccordionElems).forEach((accordion) => {
        new NestedAccordion(accordion);
      });
    }

    // MEDIA SLIDERS
    const sliderMediaElements = document.querySelectorAll('.js-slider-media');
    if (sliderMediaElements.length) {
      for (let i = 0; i < sliderMediaElements.length; i++) {
        new MediaSlider(sliderMediaElements[i]);
      }
    }

    // TIMELINE SLIDERS
    const sliderTimelineElements = document.querySelectorAll(
      '.js-slider-timeline'
    );
    if (sliderTimelineElements.length) {
      for (let i = 0; i < sliderTimelineElements.length; i++) {
        new TimelineSlider(sliderTimelineElements[i]);
      }
    }

    // SingleLightbox
    const glightboxWrapper = document.querySelectorAll('.js-glightbox-wrapper');
    if (glightboxWrapper.length) {
      for (let i = 0; i < glightboxWrapper.length; i++) {
        new SingleLightbox(glightboxWrapper[i]);
      }
    }

    const heroScroll = document.querySelector('.js-hero-scroll');
    const scrollID = document.getElementById('scroll-anchor');
    if (heroScroll) {
      heroScroll.addEventListener('click', () => {
        scrollID.scrollIntoView({ block: 'start' });
      });
    }

    // RELATED CONSTRUCTION NUMBERS SLIDERS
    const constructionNumbersSlider = document.querySelectorAll(
      '.js-slider-construction-number'
    );
    if (constructionNumbersSlider.length) {
      for (let i = 0; i < constructionNumbersSlider.length; i++) {
        new RelatedConstructionNumberSlider(constructionNumbersSlider[i]);
      }
    }

    // HOUSE TYPES SLIDERS
    const houseTypesSlider = document.querySelectorAll('.js-slider-housing');

    if (houseTypesSlider.length) {
      for (let i = 0; i < houseTypesSlider.length; i++) {
        new HouseTypesSlider(houseTypesSlider[i]);
      }
    }

    // INVOLVED COMPANIES SLIDER
    const involvedCompaniesSliders = document.querySelectorAll(
      '.js-slider-involved-companies'
    );
    if (involvedCompaniesSliders.length) {
      for (let i = 0; i < involvedCompaniesSliders.length; i++) {
        new InvolvedCompaniesSlider(involvedCompaniesSliders[i]);
      }
    }

    // Listen for click events on body
    // eslint-disable-next-line prefer-arrow-callback
    document.body.addEventListener('click', function (event) {
      if (
        menuButton &&
        !menuButton.contains(event.target) &&
        menuWrapper &&
        !menuWrapper.contains(event.target)
      ) {
        const customEvent = new CustomEvent('makeTogglePassive', {
          detail: { toggleId: 'menu-toggle' }
        });
        document.body.dispatchEvent(customEvent);
      }
    });

    // Initialize subscription settings if the form exists
    if (document.querySelector('.subscription-settings')) {
      new SubscriptionSettings();
    }
  }

  handleTimeline() {
    new Timeline();
  }

  formValidationNoScroll() {
    document.querySelector('html').style.scrollBehavior = 'auto';
    setTimeout(() => {
      document.querySelector('html').style.scrollBehavior = '';
    }, 1);
  }
}
// eslint-disable-next-line no-undef
window.Mapbox = Mapbox;
window.SelectAutocomplete = SelectAutocomplete;
window.noUiSlider = noUiSlider;
window.wNumb = wNumb;
window.mainApp = new MainApp();
